import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  AlertTriangle,
  Link2,
  Check,
  X,
  Pencil,
  Undo2,
} from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * EditableTable - Spreadsheet-style bulk editing component
 * 
 * Props:
 * - data: Array of records to display
 * - columns: Array of column configurations { key, label, editable, type, width }
 * - dependencies: Map of record_id -> dependency info
 * - selectedIds: Set of selected record IDs
 * - onSelectionChange: (ids: Set) => void
 * - onCellChange: (recordId, field, value) => void
 * - changes: Map of recordId -> { field -> newValue }
 * - entityType: string for display purposes
 */
export function EditableTable({
  data = [],
  columns = [],
  dependencies = {},
  selectedIds = new Set(),
  onSelectionChange,
  onCellChange,
  changes = {},
  entityType = "record",
}) {
  const [editingCell, setEditingCell] = useState(null); // { recordId, field }
  const [editValue, setEditValue] = useState("");

  // Check if all visible items are selected
  const allSelected = data.length > 0 && data.every(row => selectedIds.has(row.id));
  const someSelected = data.some(row => selectedIds.has(row.id)) && !allSelected;

  // Toggle all selection
  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(data.map(row => row.id)));
    }
  }, [allSelected, data, onSelectionChange]);

  // Toggle single row selection
  const handleRowSelect = useCallback((recordId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId);
    } else {
      newSelected.add(recordId);
    }
    onSelectionChange(newSelected);
  }, [selectedIds, onSelectionChange]);

  // Start editing a cell
  const startEditing = useCallback((recordId, field, currentValue) => {
    setEditingCell({ recordId, field });
    // Use changed value if exists, otherwise current value
    const changedValue = changes[recordId]?.[field];
    setEditValue(changedValue !== undefined ? changedValue : (currentValue || ""));
  }, [changes]);

  // Commit edit
  const commitEdit = useCallback(() => {
    if (editingCell) {
      onCellChange(editingCell.recordId, editingCell.field, editValue);
      setEditingCell(null);
      setEditValue("");
    }
  }, [editingCell, editValue, onCellChange]);

  // Cancel edit
  const cancelEdit = useCallback(() => {
    setEditingCell(null);
    setEditValue("");
  }, []);

  // Handle keyboard events in edit mode
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      commitEdit();
    } else if (e.key === "Escape") {
      cancelEdit();
    }
  }, [commitEdit, cancelEdit]);

  // Get display value for a cell (considering pending changes)
  const getCellValue = useCallback((record, field) => {
    const changedValue = changes[record.id]?.[field];
    if (changedValue !== undefined) {
      return changedValue;
    }
    return record[field];
  }, [changes]);

  // Check if a cell has been modified
  const isCellModified = useCallback((recordId, field) => {
    return changes[recordId]?.[field] !== undefined;
  }, [changes]);

  // Check if a row has any modifications
  const isRowModified = useCallback((recordId) => {
    return Object.keys(changes[recordId] || {}).length > 0;
  }, [changes]);

  // Get dependency info for a record
  const getDependencyInfo = useCallback((recordId) => {
    return dependencies[recordId] || { total: 0, can_delete: true, details: {} };
  }, [dependencies]);

  // Render dependency indicator
  const renderDependencyIndicator = (recordId) => {
    const depInfo = getDependencyInfo(recordId);
    if (depInfo.total === 0) return null;

    const details = depInfo.details || {};
    const tooltipContent = Object.entries(details)
      .filter(([_, count]) => count > 0)
      .map(([type, count]) => `${count} ${type.replace(/_/g, " ")}`)
      .join(", ");

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1 text-yellow-500">
              <Link2 className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">{depInfo.total}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-xs">
              <strong>Dependencies:</strong> {tooltipContent}
            </p>
            {!depInfo.can_delete && (
              <p className="text-xs text-yellow-400 mt-1">
                Cannot delete - has active references
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Render cell content based on type and edit state
  const renderCell = (record, column) => {
    const isEditing = editingCell?.recordId === record.id && editingCell?.field === column.key;
    const value = getCellValue(record, column.key);
    const modified = isCellModified(record.id, column.key);

    // Edit mode
    if (isEditing && column.editable) {
      return (
        <div className="flex items-center gap-1">
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={commitEdit}
            autoFocus
            type={(column.type === "number" || column.type === "currency") ? "number" : "text"}
            step={(column.type === "number" || column.type === "currency") ? "0.01" : undefined}
            className="h-6 text-xs py-0 px-1.5"
            data-testid={`edit-input-${record.id}-${column.key}`}
          />
          <Button
            size="icon"
            variant="ghost"
            className="h-5 w-5"
            onClick={commitEdit}
          >
            <Check className="h-2.5 w-2.5 text-green-500" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-5 w-5"
            onClick={cancelEdit}
          >
            <X className="h-2.5 w-2.5 text-red-500" />
          </Button>
        </div>
      );
    }

    // Display mode - format based on column type
    let displayValue;
    if (column.type === "array" && Array.isArray(value)) {
      displayValue = value.join(", ") || "—";
    } else if (column.type === "currency" && value !== null && value !== undefined) {
      // Format as currency with 2 decimal places
      const numValue = parseFloat(value);
      displayValue = isNaN(numValue) ? "—" : numValue.toFixed(2);
    } else if (column.type === "number" && value !== null && value !== undefined) {
      const numValue = parseFloat(value);
      displayValue = isNaN(numValue) ? "—" : numValue.toString();
    } else {
      displayValue = value || "—";
    }

    return (
      <div
        className={cn(
          "flex items-center gap-1 min-h-[22px] group text-xs",
          column.editable && "cursor-pointer hover:bg-white/5 rounded px-1 -mx-1"
        )}
        onClick={() => column.editable && startEditing(record.id, column.key, value)}
        data-testid={`cell-${record.id}-${column.key}`}
      >
        <span className={cn(
          "flex-1 truncate",
          modified && "text-blue-400 font-medium"
        )}>
          {displayValue}
        </span>
        {modified && (
          <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 bg-blue-500/10 text-blue-400 border-blue-500/30">
            Mod
          </Badge>
        )}
        {column.editable && !isEditing && (
          <Pencil className="h-2.5 w-2.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-white/10 overflow-hidden">
      <div className="overflow-x-auto">
        <Table className="text-xs">
          <TableHeader>
            <TableRow className="bg-white/5 hover:bg-white/5">
              {/* Selection checkbox column */}
              <TableHead className="w-8 px-2">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all"
                  data-testid="select-all-checkbox"
                  className="h-3.5 w-3.5"
                />
              </TableHead>
              {/* Dependency indicator column */}
              <TableHead className="w-12 text-center px-1">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Link2 className="h-3 w-3 text-muted-foreground mx-auto" />
                    </TooltipTrigger>
                    <TooltipContent>Dependencies</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              {/* Data columns */}
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  style={{ width: column.width }}
                  className={cn(
                    "text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2",
                    column.editable && "text-primary"
                  )}
                >
                  {column.label}
                  {column.editable && (
                    <Pencil className="h-2.5 w-2.5 inline-block ml-1 opacity-50" />
                  )}
                </TableHead>
              ))}
              {/* Status column */}
              <TableHead className="w-16 text-right text-[10px] px-2">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 3}
                  className="h-24 text-center text-muted-foreground text-xs"
                >
                  No {entityType}s found
                </TableCell>
              </TableRow>
            ) : (
              data.map((record) => {
                const depInfo = getDependencyInfo(record.id);
                const rowModified = isRowModified(record.id);
                const isSelected = selectedIds.has(record.id);

                return (
                  <TableRow
                    key={record.id}
                    className={cn(
                      "transition-colors",
                      isSelected && "bg-primary/5",
                      rowModified && "bg-blue-500/5"
                    )}
                    data-testid={`row-${record.id}`}
                  >
                    {/* Selection checkbox */}
                    <TableCell className="px-2 py-1.5">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleRowSelect(record.id)}
                        aria-label={`Select ${record.id}`}
                        data-testid={`select-row-${record.id}`}
                        className="h-3.5 w-3.5"
                      />
                    </TableCell>
                    {/* Dependency indicator */}
                    <TableCell className="text-center px-1 py-1.5">
                      {renderDependencyIndicator(record.id)}
                    </TableCell>
                    {/* Data cells */}
                    {columns.map((column) => (
                      <TableCell key={column.key} className="px-2 py-1.5">
                        {renderCell(record, column)}
                      </TableCell>
                    ))}
                    {/* Status indicator */}
                    <TableCell className="text-right px-2 py-1.5">
                      {rowModified ? (
                        <Badge className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 text-[10px] px-1.5 py-0">
                          Pending
                        </Badge>
                      ) : depInfo.total > 0 ? (
                        <Badge variant="outline" className="text-yellow-500 border-yellow-500/30 text-[10px] px-1.5 py-0">
                          In Use
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-green-500 border-green-500/30 text-[10px] px-1.5 py-0">
                          Safe
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default EditableTable;
